<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { Product } from '@/types/Product';
import type { Swiper as SwiperClass } from 'swiper/types';
import type { CardSearchParams, DateRange } from '@/types/SearchBar';
import 'swiper/css';
import { $t } from '@/composables/translations/useTranslate.ts';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { buildOfferUrl } from '@/utils/offers/offerUrlBuilder';
import { useDebugMode } from '@/composables/debug/useDebugMode';
import { useWishlistStore } from '@/stores/wishlist';
import { HIDE_REBATES_LISTING } from '@/constants/checkout/config';

const props = defineProps<{
  product: Product;
  searchParams?: CardSearchParams;
}>();

const { isDebugMode } = useDebugMode();
const wishlistStore = useWishlistStore();

const isFavorite = computed(() =>
  props.product.productCode ? wishlistStore.has(props.product.productCode) : false
);

const onToggleFavorite = () => {
  if (props.product.productCode) {
    wishlistStore.toggle(props.product.productCode);
  }
};

const isHovered = ref(false);
const windowWidth = ref(window.innerWidth);
const tagRefs = ref<(HTMLElement | null)[]>([]);
const tagOffsets = ref<number[]>([]);

const sortedTags = computed(() => {
  return [...(props.product.tags ?? [])].sort((a, b) => b.text.length - a.text.length);
});

const computeTagOffsets = async () => {
  await nextTick();
  const offsets: number[] = [];
  let cumulative = 0;
  for (let i = 0; i < tagRefs.value.length; i++) {
    offsets.push(cumulative);
    const el = tagRefs.value[i];
    cumulative += (el ? el.offsetHeight : 44) + 4;
  }
  tagOffsets.value = offsets;
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
  computeTagOffsets();
};

onMounted(() => {
  computeTagOffsets();
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const getTagStyle = (index: number): Record<string, string> => {
  const isMobile = windowWidth.value < 1024;
  const offset = tagOffsets.value[index] ?? index * 48;
  const style: Record<string, string> = {
    zIndex: String(12 - index),
    top: `${10 + index * 4}px`,
    transition: 'all 0.3s ease-in-out',
  };

  if (isMobile) {
    style.transform = `translateY(${offset}px)`;
    style.left = '10px';
  } else if (isHovered.value) {
    style.transform = `translateY(${offset}px)`;
    style.left = '10px';
  } else {
    style.transform = 'translateY(0)';
    style.left = `${10 + index * 4}px`;
  }

  return style;
};

const swiperInstance = ref<SwiperClass | null>(null);
const onSwiper = (swiper: SwiperClass) => {
  swiperInstance.value = swiper;
};

const slideNext = () => {
  swiperInstance.value?.slideNext();
};

const slidePrev = () => {
  swiperInstance.value?.slidePrev();
};

/**
 * Compute product URL with search parameters
 *
 * If searchParams are provided, appends date, duration, and room occupancy to the product URL.
 * Otherwise, returns the simple product slug.
 *
 * The link carries the length of the offer this card prices, not the selected
 * duration — with a duration range on the search request the two can differ,
 * and the detail page must ask for the length whose price was clicked. The
 * offer's dates ride along separately; the search window itself stays as the
 * user chose it.
 */
const productUrl = computed(() => {
  if (!props.searchParams) {
    return props.product.slug;
  }

  const { dateRange, duration, roomOccupancy, durationMode } = props.searchParams;
  const { offerStartDate, offerEndDate, offerDuration } = props.product;

  const offerRange: DateRange | undefined =
    offerStartDate && offerEndDate ? [new Date(offerStartDate), new Date(offerEndDate)] : undefined;

  return buildOfferUrl(props.product.slug, dateRange, offerDuration ?? duration, roomOccupancy, {
    durationMode,
    offerRange,
  });
});
</script>

<template>
  <a
    :href="productUrl"
    class="group"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="relative">
      <div class="flex flex-col gap-2 duration-300 text-label">
        <div
          v-for="(tag, index) in sortedTags"
          :key="tag.text"
          :ref="
            (el) => {
              tagRefs[index] = el as HTMLElement | null;
            }
          "
          :class="[
            'absolute p-2 w-fit',
            index % 3 === 0
              ? 'bg-highlight-1 text-white'
              : index % 3 === 1
                ? 'bg-white text-text-highlight-4'
                : 'bg-white text-text-highlight-5',
          ]"
          :style="getTagStyle(index)"
        >
          {{ tag.text }}
        </div>
      </div>
      <Swiper class="product-swiper" :slides-per-view="1" :space-between="0" @swiper="onSwiper">
        <SwiperSlide v-for="image in product.images" :key="image.imgPath">
          <picture class="w-full swiper-slide">
            <source :srcset="`${image.imgPath}.webp`" type="image/webp" />
            <img
              :src="`${image.imgPath}.${image.imgExt}`"
              :alt="image.imgAlt"
              class="block w-full object-cover aspect-square md:aspect-[4/5]"
              loading="lazy"
            />
          </picture>
        </SwiperSlide>

        <div
          v-if="product.images.length > 1"
          class="flex items-center gap-5 absolute bottom-2.5 left-2.5 z-[200]"
        >
          <button
            class="flex items-center justify-center w-10 h-10 product-button-left"
            @click.prevent="slidePrev"
          >
            <i class="w-6 h-5 rotate-180 bg-white icon icon-arrow"></i>
          </button>
          <button
            class="flex items-center justify-center w-10 h-10 product-button-right"
            @click.prevent="slideNext"
          >
            <i class="w-6 h-5 bg-white icon icon-arrow"></i>
          </button>
        </div>
      </Swiper>
      <div v-if="!HIDE_REBATES_LISTING && product.discount" class="absolute z-10 right-0 bottom-0">
        <VTooltip v-if="product.discountReason" placement="top">
          <div class="bg-text-highlight-2 px-[10px] py-3 flex flex-col items-center cursor-help">
            <p class="text-white text-label mb-1">
              {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
            </p>
            <p class="text-white text-price">{{ product.discount }}%</p>
          </div>

          <template #popper>
            <p>{{ product.discountReason }}</p>
          </template>
        </VTooltip>
        <div v-else class="bg-text-highlight-2 px-[10px] py-3 flex flex-col items-center">
          <p class="text-white text-price">{{ product.discount }}%</p>
        </div>
      </div>
    </div>
    <div class="md:pr-3 mt-3 md:mt-5 flex flex-col justify-between h-[400px]">
      <p class="flex items-center justify-between w-full text-label">
        {{ product.location }}
        <button
          v-if="product.productCode"
          type="button"
          class="flex items-center justify-center"
          :aria-pressed="isFavorite"
          :aria-label="
            isFavorite
              ? $t('ibe.wishlist.remove', 'Remove from wishlist')
              : $t('ibe.wishlist.add', 'Add to wishlist')
          "
          @click.prevent="onToggleFavorite"
        >
          <i
            :class="[
              'w-5 h-5 icon bg-actions-primary',
              isFavorite ? 'icon-favorite-active' : 'icon-favorite',
            ]"
          ></i>
        </button>
      </p>
      <p v-if="isDebugMode && product.productCode" class="text-xs text-gray-400 font-mono mt-1">
        productCode: {{ product.productCode }}
      </p>
      <h3 class="text-text-primary mt-[6px] mb-3 md:mt-[10px] md:mb-5">{{ product.title }}</h3>
      <p class="mb-5 md:mb-10">{{ product.description }}</p>
      <p>
        {{ $t('ibe.common.per-person-from', 'from') }}
        <span class="ml-2 text-price">{{ formatPrice(product.price, true) }}</span>
      </p>
      <div class="flex items-center gap-5 mb-3 md:mb-5 text-body-small">
        <p v-if="!HIDE_REBATES_LISTING && product.oldPrice" class="opacity-70">
          {{ $t('ibe.common.previously', 'Previously') }}
          <span class="line-through">{{ formatPrice(product.oldPrice, true) }}</span>
        </p>
        <p>{{ product.priceDescription }}</p>
      </div>
      <button class="btn-secondary">
        {{ $t('ibe.common.discover-now', 'Discover now') }}
        <i class="w-6 h-6 bg-black icon icon-arrow"></i>
      </button>
    </div>
  </a>
</template>
